import React, { useCallback, useEffect, useState } from 'react';
import {
  BillingPolicyReturn,
  DayCount,
  FeeBasis,
  InterestAccrual,
  InterestMethod,
  LateFeeType,
  LedgerEntryType,
  ProrationMethod,
  RoundingMode,
  TurnoverBalancePolicy,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { Divider } from '../common/Catalyst/divider';
import { Fieldset, FieldGroup, Legend } from '../common/Catalyst/fieldset';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage } from '../utils/billingHelpers';
import { BoolSetting, EnumSetting, TextSetting } from './BillingPolicyFields';
import PaymentOrderField from './PaymentOrderField';
import {
  dayCount,
  feeBasis,
  interestAccrual,
  interestMethod,
  lateFeeType,
  prorationMethod,
  roundingMode,
  turnoverBalancePolicy,
} from './enumLabels';
import {
  fromPolicy,
  hasErrors,
  isEdited,
  PolicyDraft,
  PolicyErrors,
  toUpdate,
  validate,
} from './policyDraft';

interface Props {
  /** Which policy row this edits. */
  scope: 'hoa' | 'company';
  scopeId: string;
  /** Owner/Admin; everyone else gets the same page, read-only. */
  canEdit: boolean;
}

/**
 * The billing policy, at either scope.
 *
 * One resource, twenty-eight fields, grouped. The read auto-creates a
 * safe-zero default so it never 404s, and the write is a dirty diff —
 * see `policyDraft.ts` for why that matters more than it looks.
 *
 * Scope-agnostic on purpose: the company row is the *template* new HOAs
 * are created from, and it is exactly the same shape. The only
 * difference is the banner, because inheritance is copy-on-create and
 * the page must not imply that editing here re-rates anything.
 */
const BillingPolicyForm: React.FC<Props> = ({ scope, scopeId, canEdit }) => {
  const [policy, setPolicy] = useState<BillingPolicyReturn | null>(null);
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [errors, setErrors] = useState<PolicyErrors>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } =
        scope === 'hoa'
          ? await billingService.getHoaPolicy({ hoaId: scopeId })
          : await billingService.getCompanyPolicy({ companyId: scopeId });
      setPolicy(data);
      setDraft(fromPolicy(data));
      setErrors({});
    } catch (err) {
      console.error('Error loading billing policy:', err);
      setAlert({
        type: 'error',
        title: "Couldn't load the billing policy",
        message: billingErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof PolicyDraft>(
    key: K,
    value: PolicyDraft[K]
  ): void => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    // Clear this field's error as soon as it is touched; re-validating
    // the whole form on every keystroke would light up fields the user
    // has not reached yet.
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const save = async (): Promise<void> => {
    if (!policy || !draft) return;
    const found = validate(draft);
    if (hasErrors(found)) {
      setErrors(found);
      setAlert({
        type: 'error',
        title: 'Some settings need fixing',
        message: 'Nothing was saved. The fields with a message are the ones.',
      });
      return;
    }

    const update = toUpdate(policy, draft);
    if (Object.keys(update).length === 0) {
      setAlert({
        type: 'success',
        title: 'Nothing to save',
        message: 'No setting on this page has changed.',
      });
      return;
    }

    try {
      setSaving(true);
      const { data } =
        scope === 'hoa'
          ? await billingService.updateHoaPolicy({
              hoaId: scopeId,
              billingPolicyUpdate: update,
            })
          : await billingService.updateCompanyPolicy({
              companyId: scopeId,
              billingPolicyUpdate: update,
            });
      setPolicy(data);
      setDraft(fromPolicy(data));
      const count = Object.keys(update).length;
      setAlert({
        type: 'success',
        title: 'Billing policy saved',
        message: `${count} setting${count === 1 ? '' : 's'} updated.`,
      });
    } catch (err) {
      console.error('Error saving billing policy:', err);
      setAlert({
        type: 'error',
        title: "Couldn't save the billing policy",
        message: billingErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!policy || !draft) {
    return (
      <ShowAlert
        alertType="error"
        alertTitle={alert?.title ?? "Couldn't load the billing policy"}
        alertMessage={alert?.message ?? 'Please try again.'}
        isVisible
        onClose={() => setAlert(null)}
        timeout={0}
        isInline
      />
    );
  }

  const ro = !canEdit;
  // Enabled on edited, not on writable — see `isEdited`. A required
  // field left blank writes nothing, and a save button that silently
  // will not press is worse than one that presses and explains.
  const edited = isEdited(policy, draft);
  // A percentage late fee is a percentage of the basis; a flat one is
  // dollars. Same column, two different things, and the label has to
  // follow the type or somebody enters 25 meaning $25 and charges 25%.
  const percentFee = draft.late_fee_type === 'percent';

  return (
    <div>
      {alert && (
        <div className="mb-4">
          <ShowAlert
            alertType={alert.type}
            alertTitle={alert.title}
            alertMessage={alert.message}
            isVisible={!!alert}
            onClose={() => setAlert(null)}
            timeout={alert.type === 'success' ? 5000 : 12000}
            isInline
          />
        </div>
      )}

      {scope === 'company' && (
        <div className="mb-6 rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 p-4">
          <Subheading>These are defaults for new associations</Subheading>
          <Text className="mt-1">
            An association takes a <strong>copy</strong> of these when it is
            created, and edits its own from then on.{' '}
            <strong>
              Changing anything here does not affect a single existing
              association
            </strong>{' '}
            — that is deliberate, so nobody re-rates forty of them by editing
            one field. To roll a real change out, change it on each association.
          </Text>
        </div>
      )}

      {ro && (
        <div className="mb-6">
          <Text>
            You can read this association&rsquo;s billing policy but not change
            it. Changing the rules that decide what homeowners are charged is
            limited to an Owner or Admin.
          </Text>
        </div>
      )}

      <Fieldset disabled={ro}>
        <Legend>Late fees</Legend>
        <Text className="mt-1">
          What happens when a charge goes unpaid past its grace period.
        </Text>
        <FieldGroup>
          <TextSetting
            label="Grace period"
            help="Days after the due date before a charge counts as late. Several states set a statutory minimum."
            suffix="days"
            value={draft.grace_period_days}
            error={errors.grace_period_days}
            onChange={(v) => set('grace_period_days', v)}
            disabled={ro}
          />
          <EnumSetting
            label="Late fee type"
            value={draft.late_fee_type}
            options={lateFeeType}
            error={errors.late_fee_type}
            onChange={(v) => set('late_fee_type', v as LateFeeType)}
            disabled={ro}
          />

          {draft.late_fee_type !== 'none' && (
            <>
              <TextSetting
                label={percentFee ? 'Late fee percentage' : 'Late fee amount'}
                help={
                  percentFee
                    ? 'A percentage of the basis below — 5 means 5%, not $5.'
                    : 'A flat amount in dollars, whatever the charge was.'
                }
                prefix={percentFee ? undefined : '$'}
                suffix={percentFee ? '%' : undefined}
                value={draft.late_fee_amount}
                error={errors.late_fee_amount}
                onChange={(v) => set('late_fee_amount', v)}
                disabled={ro}
              />
              <EnumSetting
                label="Charged against"
                value={draft.late_fee_basis}
                options={feeBasis}
                onChange={(v) => set('late_fee_basis', v as FeeBasis)}
                disabled={ro}
              />
              <TextSetting
                label="Most per period"
                help="Blank means no cap on what one period can add."
                prefix="$"
                placeholder="No cap"
                value={draft.late_fee_max_per_period}
                error={errors.late_fee_max_per_period}
                onChange={(v) => set('late_fee_max_per_period', v)}
                disabled={ro}
              />
              <TextSetting
                label="Most in total"
                help="Blank means no lifetime cap on late fees for a charge."
                prefix="$"
                placeholder="No cap"
                value={draft.late_fee_cap_total}
                error={errors.late_fee_cap_total}
                onChange={(v) => set('late_fee_cap_total', v)}
                disabled={ro}
              />
              <BoolSetting
                label="Charge it again each period"
                help="Off: charged once per overdue charge. On: charged again every period it stays overdue."
                value={draft.late_fee_recurs}
                onChange={(v) => set('late_fee_recurs', v)}
                disabled={ro}
              />
              <BoolSetting
                label="Late fees can themselves go late"
                help="On: an unpaid late fee accrues further late fees. Capped by statute in several states, and rarely what an association wants."
                value={draft.late_fee_compounds}
                onChange={(v) => set('late_fee_compounds', v)}
                disabled={ro}
              />
            </>
          )}

          <TextSetting
            label="Returned payment (NSF) fee"
            help="Charged when a payment bounces. Capped by statute in several states. Blank means none."
            prefix="$"
            placeholder="None"
            value={draft.nsf_fee}
            error={errors.nsf_fee}
            onChange={(v) => set('nsf_fee', v)}
            disabled={ro}
          />
        </FieldGroup>
      </Fieldset>

      <Divider className="my-10" />

      <Fieldset disabled={ro}>
        <Legend>Interest</Legend>
        <FieldGroup>
          <BoolSetting
            label="Charge interest on overdue balances"
            value={draft.interest_enabled}
            onChange={(v) => set('interest_enabled', v)}
            disabled={ro}
          />
          {draft.interest_enabled && (
            <>
              <TextSetting
                label="Annual rate"
                help="A percentage, not a fraction: 12 means 12% a year. Rounding happens once, when the entry is written, never per day."
                suffix="%"
                value={draft.interest_annual_rate}
                error={errors.interest_annual_rate}
                onChange={(v) => set('interest_annual_rate', v)}
                disabled={ro}
              />
              <EnumSetting
                label="Method"
                value={draft.interest_method}
                options={interestMethod}
                onChange={(v) => set('interest_method', v as InterestMethod)}
                disabled={ro}
              />
              <EnumSetting
                label="Accrues"
                value={draft.interest_accrual}
                options={interestAccrual}
                onChange={(v) => set('interest_accrual', v as InterestAccrual)}
                disabled={ro}
              />
              <EnumSetting
                label="Charged against"
                value={draft.interest_basis}
                options={feeBasis}
                onChange={(v) => set('interest_basis', v as FeeBasis)}
                disabled={ro}
              />
              <EnumSetting
                label="Day count"
                value={draft.interest_day_count}
                options={dayCount}
                onChange={(v) => set('interest_day_count', v as DayCount)}
                disabled={ro}
              />
            </>
          )}
        </FieldGroup>
      </Fieldset>

      <Divider className="my-10" />

      <Fieldset disabled={ro}>
        <Legend>Payments</Legend>
        <FieldGroup>
          <BoolSetting
            label="Allow credit balances"
            help="Off: an overpayment is held on the payment, visible and refundable, and the balance never goes below zero. On: the remainder posts as a credit and applies to the next charge."
            value={draft.allow_credit_balances}
            onChange={(v) => set('allow_credit_balances', v)}
            disabled={ro}
          />
          <div>
            <Subheading level={4}>Apply payments to</Subheading>
            <Text className="mt-1">
              Which kind of charge a payment clears first. Mandated by statute
              in several states — and not the same one.
            </Text>
            <PaymentOrderField
              value={draft.payment_application_order}
              onChange={(v) =>
                set('payment_application_order', v as LedgerEntryType[])
              }
              disabled={ro}
            />
          </div>
          <EnumSetting
            label="Rounding"
            help="Applied once, at the moment a fee or interest entry is written."
            value={draft.rounding_mode}
            options={roundingMode}
            onChange={(v) => set('rounding_mode', v as RoundingMode)}
            disabled={ro}
          />
          <EnumSetting
            label="Proration"
            help="How a part period is charged when a home or an owner arrives mid-period. Full periods are never prorated."
            value={draft.proration_method}
            options={prorationMethod}
            onChange={(v) => set('proration_method', v as ProrationMethod)}
            disabled={ro}
          />
        </FieldGroup>
      </Fieldset>

      <Divider className="my-10" />

      <Fieldset disabled={ro}>
        <Legend>Collections</Legend>
        <Text className="mt-1">
          When an account is held, and how far the nightly job may take a case
          on its own. It never goes past a late notice; demand, attorney, lien
          and foreclosure are decisions a person makes.
        </Text>
        <FieldGroup>
          <TextSetting
            label="Hold after"
            help="Days past due before an account is flagged HOLD on the payer file. Blank means days alone never trigger it."
            suffix="days"
            placeholder="No threshold"
            value={draft.hold_threshold_days}
            error={errors.hold_threshold_days}
            onChange={(v) => set('hold_threshold_days', v)}
            disabled={ro}
          />
          <TextSetting
            label="Hold over"
            help="Balance at which an account is flagged HOLD. Blank means the amount alone never triggers it."
            prefix="$"
            placeholder="No threshold"
            value={draft.hold_threshold_amount}
            error={errors.hold_threshold_amount}
            onChange={(v) => set('hold_threshold_amount', v)}
            disabled={ro}
          />
          <TextSetting
            label="Late notice after"
            help="Days past due before a case reaches a late notice. Blank leaves it to the notice schedule."
            suffix="days"
            placeholder="Not set"
            value={draft.days_to_late_notice}
            error={errors.days_to_late_notice}
            onChange={(v) => set('days_to_late_notice', v)}
            disabled={ro}
          />
          <TextSetting
            label="Demand after"
            help="Days past due before a case is ready for a demand letter. A person still has to send it."
            suffix="days"
            placeholder="Not set"
            value={draft.days_to_demand}
            error={errors.days_to_demand}
            onChange={(v) => set('days_to_demand', v)}
            disabled={ro}
          />
        </FieldGroup>
      </Fieldset>

      <Divider className="my-10" />

      <Fieldset disabled={ro}>
        <Legend>Turnover</Legend>
        <FieldGroup>
          <EnumSetting
            label="Unpaid balance when a home sells"
            help="Whether the seller's unpaid balance follows the property to the buyer or stays with them. This varies by state and by the association's declaration — it is a legal question, not a preference."
            value={draft.turnover_balance_policy}
            options={turnoverBalancePolicy}
            onChange={(v) =>
              set('turnover_balance_policy', v as TurnoverBalancePolicy)
            }
            disabled={ro}
          />
        </FieldGroup>
      </Fieldset>

      <Divider className="my-10" />

      <Fieldset disabled={ro}>
        <Legend>Estoppel certificates</Legend>
        <Text className="mt-1">
          The document a title company asks for at closing. Fees and turnaround
          are capped by statute in several states.
        </Text>
        <FieldGroup>
          <TextSetting
            label="Fee"
            help="Charged to the home's ledger when a certificate is issued. Blank means no fee."
            prefix="$"
            placeholder="No fee"
            value={draft.estoppel_fee}
            error={errors.estoppel_fee}
            onChange={(v) => set('estoppel_fee', v)}
            disabled={ro}
          />
          <TextSetting
            label="Rush fee"
            help="Charged instead of the standard fee when a certificate is requested as a rush."
            prefix="$"
            placeholder="No rush fee"
            value={draft.estoppel_fee_rush}
            error={errors.estoppel_fee_rush}
            onChange={(v) => set('estoppel_fee_rush', v)}
            disabled={ro}
          />
          <TextSetting
            label="Good through"
            help="How long a certificate's figures stand. Dues keep accruing, so a certificate without an expiry is wrong the day after it is issued."
            suffix="days"
            value={draft.estoppel_good_through_days}
            error={errors.estoppel_good_through_days}
            onChange={(v) => set('estoppel_good_through_days', v)}
            disabled={ro}
          />
        </FieldGroup>
      </Fieldset>

      <Divider className="my-10" />

      <Fieldset disabled={ro}>
        <Legend>Billing runs</Legend>
        <FieldGroup>
          <TextSetting
            label="Hold a run that moves more than"
            help="A scheduled run whose total differs from the last posted period by more than this stays unposted for review. Holding is the safe failure: a held run is one click from correct, while a wrongly posted one means reversing every entry after the coupons have gone out."
            suffix="%"
            value={draft.variance_threshold_percent}
            error={errors.variance_threshold_percent}
            onChange={(v) => set('variance_threshold_percent', v)}
            disabled={ro}
          />
        </FieldGroup>
      </Fieldset>

      {canEdit && (
        <>
          <Divider className="my-10" />
          <div className="flex items-center gap-3">
            <Button color="sky" onClick={save} disabled={saving || !edited}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              plain
              disabled={saving || !edited}
              onClick={() => {
                setDraft(fromPolicy(policy));
                setErrors({});
              }}
            >
              Discard changes
            </Button>
            {!edited && <Text>No changes to save.</Text>}
          </div>
        </>
      )}
    </div>
  );
};

export default BillingPolicyForm;
